/**
 * @module TTProductTabsCustom.View
 * PDP view: custom tabs fed from the PDP component (getItemInfo).
 *
 * Injection point: the `data-view` placeholder must match addChildView registration
 * (see TTProductTabsCustom.js). In this theme it aligns with
 * `TavanoTeamProductDetailsInfo.Tabs` in product_details_full.tpl (same string as in TTProductTabsCustom.js) —
 * no theme change is needed if that div already exists.
 *
 * Tab titles: edit TAB_LABEL_KEYS or use NetSuite translation strings for the same keys.
 *
 * Below 768px the horizontal tab bar is hidden and each section uses an accordion header
 * (`product-tabs-custom__accordion-trigger`); the same `selectTab` / `activeTabId` state drives both.
 */
define('TTProductTabsCustom.View'
,	[
		'tt_product_tabs_custom.tpl'
	,	'Backbone'
	,	'Utils'
	,	'TTProductTabsCustom.Mapper'
	,	'jQuery'
	]
,	function (
		tt_product_tabs_custom_tpl
	,	Backbone
	,	Utils
	,	Mapper
	,	jQuery
	)
{
	'use strict';

	/** Set to false before production deploy */
	var VIEW_DEBUG_LOG = true;

	var VIEW_LOG_PREFIX = '[TTProductTabsCustom.View]';

	/** Namespace for ESC handler so it can be removed without colliding with other listeners */
	var VIDEO_MODAL_ESC_NS = 'keyup.productTabsCustomVideo';

	function viewDebugLog(message, payload)
	{
		if (!VIEW_DEBUG_LOG || typeof console === 'undefined' || !console.log)
		{
			return;
		}
		if (payload !== undefined)
		{
			console.log(VIEW_LOG_PREFIX + ' ' + message, payload);
		}
		else
		{
			console.log(VIEW_LOG_PREFIX + ' ' + message);
		}
	}

	function viewDebugWarn(message, payload)
	{
		if (!VIEW_DEBUG_LOG || typeof console === 'undefined' || !console.warn)
		{
			return;
		}
		if (payload !== undefined)
		{
			console.warn(VIEW_LOG_PREFIX + ' ' + message, payload);
		}
		else
		{
			console.warn(VIEW_LOG_PREFIX + ' ' + message);
		}
	}

	/**
	 * Keys passed to Utils.translate for tab titles.
	 * Change labels here or manage the same keys in Translation / SMT.
	 */
	var TAB_LABEL_KEYS = {};
	TAB_LABEL_KEYS[Mapper.TAB_IDS.DESCRIPTION] = 'Description';
	TAB_LABEL_KEYS[Mapper.TAB_IDS.FEATURES_AND_SPECS] = 'Features & Specifications';
	TAB_LABEL_KEYS[Mapper.TAB_IDS.DOCUMENTS] = 'Documents';
	TAB_LABEL_KEYS[Mapper.TAB_IDS.VIDEOS] = 'Videos';

	/**
	 * Backbone.View (not SCView): in some SCS deployments the `SCView` module does not expose `.extend`
	 * and fails with "SCView.extend is not a function".
	 */
	return Backbone.View.extend({

		template: tt_product_tabs_custom_tpl

	,	render: function render ()
		{
			/* Modal DOM is replaced; always drop ESC listener to avoid duplicates */
			jQuery(document).off(VIDEO_MODAL_ESC_NS);
			var ctx = this.getContext();
			this.$el.html(this.template(ctx));
			this.delegateEvents();
			return this;
		}

	,	initialize: function initialize (options)
		{
			this.applicationContainer = options && options.container;
			this.activeTabId = null;
			/** Mobile accordion: user closed all sections (activeTabId null); desktop getContext clears this. */
			this.allowNoActiveTab = false;

			var self = this;
			this._videoModalEscHandler = function (evt)
			{
				if (!evt)
				{
					return;
				}
				if (evt.key === 'Escape' || evt.keyCode === 27)
				{
					self.closeVideoModal();
				}
			};

			var pdp = this.applicationContainer && this.applicationContainer.getComponent('PDP');
			this.pdpComponent = pdp;
			if (pdp && typeof pdp.on === 'function')
			{
				this.pdpAfterOptionSelectionHandler = function ()
				{
					self.syncActiveTabAfterItemChange();
					self.render();
				};
				pdp.on('afterOptionSelection', this.pdpAfterOptionSelectionHandler);

				this.pdpAfterShowContentHandler = function ()
				{
					viewDebugLog('PDP afterShowContent → render() (item is often available here if getItemInfo was null before)');
					self.render();
				};
				pdp.on('afterShowContent', this.pdpAfterShowContentHandler);
			}
		}

	,	remove: function remove ()
		{
			jQuery(document).off(VIDEO_MODAL_ESC_NS);
			var pdp = this.pdpComponent;
			if (pdp && typeof pdp.off === 'function')
			{
				if (this.pdpAfterOptionSelectionHandler)
				{
					pdp.off('afterOptionSelection', this.pdpAfterOptionSelectionHandler);
				}
				if (this.pdpAfterShowContentHandler)
				{
					pdp.off('afterShowContent', this.pdpAfterShowContentHandler);
				}
			}
			return Backbone.View.prototype.remove.call(this);
		}

	,	syncActiveTabAfterItemChange: function syncActiveTabAfterItemChange ()
		{
			this.allowNoActiveTab = false;
			var item = this.getItemFromPdp();
			var data = Mapper.normalizeProductTabsData(item);
			if (!data.tabs || !data.tabs.length)
			{
				this.activeTabId = null;
				return;
			}
			var stillValid = false;
			for (var i = 0; i < data.tabs.length; i++)
			{
				if (data.tabs[i].id === this.activeTabId)
				{
					stillValid = true;
					break;
				}
			}
			if (!stillValid)
			{
				this.activeTabId = data.defaultActiveId;
			}
		}

	,	getItemFromPdp: function getItemFromPdp ()
		{
			if (!this.applicationContainer)
			{
				viewDebugLog('getItemFromPdp: missing applicationContainer');
				return null;
			}
			var pdp = this.applicationContainer.getComponent('PDP');
			if (!pdp || typeof pdp.getItemInfo !== 'function')
			{
				viewDebugLog('getItemFromPdp: missing PDP component or getItemInfo');
				return null;
			}
			var info = pdp.getItemInfo();

			viewDebugLog('PDP.getItemInfo() full result:', info);

			if (!info)
			{
				viewDebugLog('getItemFromPdp: getItemInfo() returned null/undefined (PDP not ready yet or not PDP view)');
				return null;
			}

			var rawItem = info.item;
			if (rawItem == null && info.line && info.line.item)
			{
				rawItem = info.line.item;
				viewDebugLog('getItemFromPdp: using info.line.item (info.item was empty)');
			}

			if (rawItem == null)
			{
				viewDebugLog('getItemFromPdp: empty item → info.item and info.line.item missing. Keys on info:', info && typeof info === 'object' ? Object.keys(info) : info);
				return null;
			}

			return rawItem;
		}

	,	events: {
			'click [data-action="product-tabs-custom-select"]': 'selectTab'
		,	'click [data-action="product-tabs-open-video"]': 'onOpenVideoModal'
		,	'click [data-action="product-tabs-video-close"]': 'onCloseVideoModal'
		,	'click [data-action="product-tabs-video-overlay"]': 'onCloseVideoModal'
		}

	,	selectTab: function selectTab (e)
		{
			e.preventDefault();
			var id = jQuery(e.currentTarget).data('tab-id');
			if (!id)
			{
				return;
			}
			var isMobile =
				typeof window !== 'undefined'
			&&	window.matchMedia
			&&	window.matchMedia('(max-width: 767px)').matches;
			if (id === this.activeTabId)
			{
				if (isMobile)
				{
					this.activeTabId = null;
					this.allowNoActiveTab = true;
					this.render();
				}
				return;
			}
			this.allowNoActiveTab = false;
			this.activeTabId = id;
			this.render();
		}

	,	onOpenVideoModal: function onOpenVideoModal (e)
		{
			e.preventDefault();
			var $btn = jQuery(e.currentTarget);
			var rawId = $btn.attr('data-youtube-id');
			var videoTitle = $btn.attr('data-video-title') || '';
			var id = Mapper.normalizeYoutubeVideoId(rawId);
			if (!id)
			{
				viewDebugWarn('onOpenVideoModal: invalid or missing YouTube id (expected ID only in custitem_tt_video_src_*)', rawId);
				return;
			}
			var embedUrl = Mapper.buildYoutubeEmbedUrl(id);
			if (!embedUrl)
			{
				viewDebugWarn('onOpenVideoModal: could not build embed URL', id);
				return;
			}

			var $modal = this.$('[data-tt-video-modal]');
			var $iframe = this.$('[data-tt-video-modal-iframe]');
			var $titleEl = this.$('[data-tt-video-modal-title]');
			if (!$modal.length || !$iframe.length)
			{
				viewDebugWarn('onOpenVideoModal: modal markup missing (showBlock false?)');
				return;
			}

			$iframe.attr('src', embedUrl);
			$titleEl.text(videoTitle);
			$titleEl.toggleClass('product-tabs-custom__video-modal-title--is-empty', !videoTitle);

			$modal.addClass('is-open');
			$modal.attr('aria-hidden', 'false');

			jQuery(document).off(VIDEO_MODAL_ESC_NS).on(VIDEO_MODAL_ESC_NS, this._videoModalEscHandler);
		}

	,	onCloseVideoModal: function onCloseVideoModal (e)
		{
			if (e)
			{
				e.preventDefault();
			}
			this.closeVideoModal();
		}

	/**
	 * Clears iframe src and hides modal so playback stops (YouTube iframe must lose src).
	 */
	,	closeVideoModal: function closeVideoModal ()
		{
			jQuery(document).off(VIDEO_MODAL_ESC_NS);
			var $modal = this.$('[data-tt-video-modal]');
			var $iframe = this.$('[data-tt-video-modal-iframe]');
			if ($iframe.length)
			{
				$iframe.removeAttr('src');
			}
			if ($modal.length)
			{
				$modal.removeClass('is-open');
				$modal.attr('aria-hidden', 'true');
			}
		}

	,	buildTabModels: function buildTabModels (data, activeId)
		{
			var list = [];
			var tabIds = data.tabs || [];
			for (var i = 0; i < tabIds.length; i++)
			{
				var tid = tabIds[i].id;
				var labelKey = TAB_LABEL_KEYS[tid] || tid;
				var model = {
					id: tid
				,	label: Utils.translate(labelKey)
				,	isActive: tid === activeId
				};
				if (tid === Mapper.TAB_IDS.DESCRIPTION)
				{
					model.isDescription = true;
					model.html = data.description.html;
					model.hasProductCategory = !!(data.description.hasProductCategory);
					model.productCategoryLabels = data.description.productCategoryLabels || [];
				}
				else if (tid === Mapper.TAB_IDS.FEATURES_AND_SPECS)
				{
					model.isFeaturesAndSpecs = true;
					model.sections = data.specificationsSections;
					model.hasSpecifications = !!(data.specificationsSections && data.specificationsSections.length);
					model.featuresHtml = data.features.html;
					model.hasFeatures = !!(data.features && data.features.hasContent);
				}
				else if (tid === Mapper.TAB_IDS.DOCUMENTS)
				{
					model.isDocuments = true;
					model.documents = data.documents;
				}
				else if (tid === Mapper.TAB_IDS.VIDEOS)
				{
					model.isVideos = true;
					model.videos = data.videos;
				}
				list.push(model);
			}
			return list;
		}

	,	getContext: function getContext ()
		{
			var self = this;
			var item = this.getItemFromPdp();
			var data = Mapper.normalizeProductTabsData(item);

			var showBlock = !!(data.tabs && data.tabs.length);
			viewDebugLog('getContext: showBlock, data.tabs.length, defaultActiveId', {
				showBlock: showBlock
			,	tabsLength: data.tabs ? data.tabs.length : 0
			,	tabIds: (data.tabs || []).map(function (t) { return t.id; })
			,	defaultActiveId: data.defaultActiveId
			,	emptyMainHint: !showBlock ? 'data.tabs (no tab with content) or null item — see Mapper logs' : null
			});

			if (!data.tabs || !data.tabs.length)
			{
				return { showBlock: false };
			}

			var isMobile =
				typeof window !== 'undefined'
			&&	window.matchMedia
			&&	window.matchMedia('(max-width: 767px)').matches;
			if (!isMobile && this.allowNoActiveTab)
			{
				this.allowNoActiveTab = false;
				this.activeTabId = data.defaultActiveId;
			}

			if (
				this.allowNoActiveTab
			&&	(this.activeTabId === null || this.activeTabId === '')
			)
			{
				/* Mobile: all accordion sections collapsed */
			}
			else if (!this.activeTabId || !data.tabs.some(function (t) { return t.id === self.activeTabId; }))
			{
				this.allowNoActiveTab = false;
				this.activeTabId = data.defaultActiveId;
			}

			var tabs = this.buildTabModels(data, this.activeTabId);

			return {
				showBlock: true
			,	tabs: tabs
			};
		}
	});
});
